import React, { useState } from 'react';
import {
    Box, SimpleGrid, Text, Image, HStack, Center,
    VStack, Button, Spinner, Modal, ModalOverlay,
    ModalContent, ModalHeader, ModalCloseButton,
    ModalBody, ModalFooter, Grid, GridItem, Card,
    CardHeader, CardBody,Input
} from '@chakra-ui/react';

import {  FaHeart, FaComment, FaInstagram,FaCalendarAlt, FaPlay } from 'react-icons/fa';

import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
// 型定義
export type MediaPost = {
    id: string;
    media_type: string;
    media_url: string;
    timestamp?: string;
    permalink: string;
    caption?: string;
    impressions?: number;
    like_count?: number;
    comments_count?: number;
    saved?: number;
    reach?: number;
    profile_picture_url?: string;
    username?: string;
    views?: number;
    shares?: number;
    replies?: number;
    follows?: number;
    total_interactions?: number;
};

interface InstagramPostsGridProps {
    mediaData: MediaPost[];
    isLoading: boolean;
    title?: string;
    onFilterChange?: (displayMode: 'images' | 'videos') => void;
    onDateRangeChange?: (startDate: string, endDate: string) => void;
    isInstagramLogin: boolean;   // ★ここを追加！
    layoutMode?: 'default' | 'carousel';
}

const truncateCaption = (caption: string | undefined, length: number = 100): string => {
    if (!caption) return 'No caption';
    return caption.length > length ? `${caption.substring(0, length)}...` : caption;
};

const InstagramPostsGrid: React.FC<InstagramPostsGridProps> = ({
                                                                   mediaData,
                                                                   isLoading,
                                                                   title = "リーチ別のトップコンテンツ",
                                                                   onFilterChange,
                                                                   onDateRangeChange,
                                                                   isInstagramLogin,
                                                                   layoutMode = 'default',
                                                               }) => {
    // 直近1ヶ月の初期値を設定
    const [startDate, setStartDate] = useState<string>(() => {
        const today = new Date();
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(today.getMonth() - 1);
        return oneMonthAgo.toISOString().split('T')[0];
    });
    
    const [endDate, setEndDate] = useState<string>(() => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    });

    const [displayMode, setDisplayMode] = useState<'images' | 'videos'>('images');
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [selectedPost, setSelectedPost] = useState<MediaPost | null>(null);

    // 初期値を親コンポーネントに通知
    React.useEffect(() => {
        if (onDateRangeChange && startDate && endDate) {
            onDateRangeChange(startDate, endDate);
        }
    }, []); // 初回のみ実行

    // フィルターとソート処理を結合
    const filteredMedia = mediaData
        .filter(post => {
            const type = post.media_type?.toUpperCase() || '';
            const isCorrectType = displayMode === 'images'
                ? (type === 'IMAGE' || type === 'CAROUSEL_ALBUM')
                : (type === 'VIDEO' || type === 'REEL');

            if (!isCorrectType) return false;

            // 日付フィルター（初期値が設定されているため適用される）
            if (!post.timestamp) return true;

            try {
                const postDate = new Date(post.timestamp);
                return (!startDate || postDate >= new Date(startDate)) &&
                       (!endDate || postDate <= new Date(endDate));
            } catch (e) {
                console.error('日付変換エラー:', e);
                return true;
            }
        })
        .sort((a, b) => {
            // いいね数でソート（多い順）
            const aLikes = a.like_count || 0;
            const bLikes = b.like_count || 0;
            return bLikes - aLikes;
        });

    // フィルター変更時の処理
    const handleDisplayModeChange = (mode: 'images' | 'videos') => {
        setDisplayMode(mode);
        if (onFilterChange) onFilterChange(mode);
    };

    // 日付範囲変更時の処理
    const handleDateRangeChange = (start: string, end: string) => {
        setStartDate(start);
        setEndDate(end);
        if (onDateRangeChange) onDateRangeChange(start, end);
    };

    // 残りのコードは同じ...
    // モーダル管理
    const openModal = (postId: string) => {
        const post = mediaData.find((item) => item.id === postId);
        if (post) {
            setSelectedPost(post);
            setIsModalOpen(true);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedPost(null);
    };
    return (
        <Box p={4}>
            <Box>
                <Box>
                    <HStack spacing={1} alignItems="center">
                        <FaInstagram  />
                        <Text fontWeight="bold" fontSize="xl">{title}</Text>
                    </HStack>
                    {(onDateRangeChange || onFilterChange) && (
                        <>

                            <HStack spacing={2} mb={1} p={2} borderRadius="md">
                                <Input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => handleDateRangeChange(e.target.value, endDate)}
                                    placeholder="開始日"
                                    maxW="200px"
                                    bg="white"
                                />
                                <Box>〜</Box>
                                <Input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => handleDateRangeChange(startDate, e.target.value)}
                                    placeholder="終了日"
                                    maxW="200px"
                                    bg="white"
                                />

                                <Button size="sm"
                                        variant="ghost" onClick={() => handleDateRangeChange('', '')}>
                                    クリア
                                </Button>
                            </HStack>
                            <HStack spacing={4} mt={4} mb={4} ml={2}>
                                <Button
                                    onClick={() => handleDisplayModeChange('images')}
                                    colorScheme={displayMode === 'images' ? 'blue' : 'gray'}
                                >
                                    画像
                                </Button>
                                <Button
                                    onClick={() => handleDisplayModeChange('videos')}
                                    colorScheme={displayMode === 'videos' ? 'blue' : 'gray'}
                                >
                                    動画
                                </Button>
                            </HStack>
                        </>
                    )}
                </Box>

                <Box>
                    {isLoading ? (
                        <Center h="200px">
                            <Spinner size="xl" color="blue.500" />
                        </Center>
                    ) : mediaData.length > 0 ? (
                        layoutMode === 'carousel' ? (
                            <Box overflowX="auto" whiteSpace="nowrap" px={2}>
                                <HStack spacing={4} align="start">
                                    {filteredMedia.map((post) => (
                                        <Box
                                            key={post.id}
                                            minW="220px"
                                            maxW="220px"
                                            h="360px"
                                            flexShrink={0}
                                            p={4}
                                            bg="white"
                                            borderRadius="md"
                                            boxShadow="md"
                                            cursor="pointer"
                                            onClick={() => openModal(post.id)}
                                            display="flex"
                                            flexDirection="column"
                                            justifyContent="space-between"
                                        >
                                            <Box>
                                                {/* メディア表示（画像 or 動画） */}
                                                {post.media_type === "VIDEO" || post.media_type === "REEL" ? (
                                                    <video
                                                        controls
                                                        width="100%"
                                                        height="140px"
                                                        style={{ objectFit: "cover", borderRadius: "6px" }}
                                                    >
                                                        <source src={post.media_url} type="video/mp4" />
                                                        お使いのブラウザは video タグをサポートしていません。
                                                    </video>
                                                ) : (
                                                    <Image
                                                        src={post.media_url}
                                                        alt={post.caption || "No caption"}
                                                        width="100%"
                                                        height="140px"
                                                        objectFit="cover"
                                                        borderRadius="md"
                                                    />
                                                )}

                                                {/* キャプション */}
                                                <Text
                                                    fontSize="sm"
                                                    mt={2}
                                                    noOfLines={3}
                                                    whiteSpace="normal"
                                                    overflowWrap="break-word"
                                                >
                                                    {truncateCaption(post.caption, 80)}
                                                </Text>
                                            </Box>

                                            {/* エンゲージメント統計 */}
                                            <VStack spacing={1} align="stretch" mt={2}>
                                                {isInstagramLogin && (
                                                    <>
                                                        <HStack justify="flex-start" fontSize="xs">
                                                            <HStack>
                                                                <FaHeart  />
                                                                <Text>{post.like_count || 0}</Text>
                                                            </HStack>
                                                            <HStack>
                                                                <FaComment  />
                                                                <Text>{post.comments_count || 0}</Text>
                                                            </HStack>
                                                        </HStack>
                                                        {/*<HStack justify="space-between" fontSize="xs">*/}
                                                        {/*    <Text color="gray.600">*/}
                                                        {/*        リーチ: {post.reach || 0}*/}
                                                        {/*    </Text>*/}
                                                        {/*    <Text color="gray.600">*/}
                                                        {/*        インプレッション: {post.impressions || 0}*/}
                                                        {/*    </Text>*/}
                                                        {/*</HStack>*/}
                                                    </>
                                                )}
                                            </VStack>
                                        </Box>
                                    ))}
                                </HStack>
                            </Box>
                        ) : (
                            // 既存のグリッド表示
                            <SimpleGrid columns={[1, 2, 3]} spacing={6}>
                                {filteredMedia.map((post) => (
                                    <Card key={post.id} cursor="pointer" onClick={() => openModal(post.id)}>
                                        {/* カード内容 */}
                                        <CardBody>
                                            {post.media_type === "VIDEO" || post.media_type === "REEL" ? (
                                                <video controls width="100%">
                                                    <source src={post.media_url} type="video/mp4" />
                                                </video>
                                            ) : (
                                                <Image src={post.media_url} alt={post.caption || "No caption"} />
                                            )}
                                            <Text mt={2}>{truncateCaption(post.caption)}</Text>
                                            {isInstagramLogin && (
                                                <HStack mt={2} spacing={4}>
                                                    <HStack>
                                                        <FaHeart color="red" />
                                                        <Text>{post.like_count || 0}</Text>
                                                    </HStack>
                                                    <HStack>
                                                        <FaComment color="blue" />
                                                        <Text>{post.comments_count || 0}</Text>
                                                    </HStack>
                                                </HStack>
                                            )}
                                        </CardBody>
                                    </Card>
                                ))}
                            </SimpleGrid>
                        )
                    ) : (
                        <Center h="200px">
                            <Text color="gray.500">表示するデータがありません</Text>
                        </Center>
                    )}
                </Box>
            </Box>

            {/* モーダル */}
            {selectedPost && (
                <Modal isOpen={isModalOpen} onClose={closeModal} size="xl">
                    <ModalOverlay />
                    <ModalContent maxW="800px">
                        <ModalHeader>投稿詳細</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            {selectedPost.media_type === "VIDEO" || selectedPost.media_type === "REEL" ? (
                                <video controls width="100%">
                                    <source src={selectedPost.media_url} type="video/mp4" />
                                </video>
                            ) : (
                                <Image src={selectedPost.media_url} alt={selectedPost.caption || "No caption"} />
                            )}
                            <Text mt={4}>{selectedPost.caption || 'No caption'}</Text>
                            {isInstagramLogin && (
                                <VStack spacing={2} align="start" mt={4}>
                                    <HStack>
                                        <FaHeart color="red" />
                                        <Text>いいね: {selectedPost.like_count || 0}</Text>
                                    </HStack>
                                    <HStack>
                                        <FaComment color="blue" />
                                        <Text>コメント: {selectedPost.comments_count || 0}</Text>
                                    </HStack>
                                    {/*<Text>リーチ: {selectedPost.reach || 0}</Text>*/}
                                    {/*<Text>インプレッション: {selectedPost.impressions || 0}</Text>*/}
                                </VStack>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button onClick={closeModal}>閉じる</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            )}
        </Box>
    );
};

export default InstagramPostsGrid;